import { ListService, PagedResultDto } from '@abp/ng.core';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Temporal } from '@js-temporal/polyfill';
import { EnumDto, OrderStatus } from '@proxy/enums';
import { GetOrderDto, OrderDto, OrderService } from '@proxy/orders';

interface Order {
  id: number;
  wineryName: string;
  status: string;
  itemCount: number;
  totalPrice: number;
  orderNumber: string;
  clientName: string;
  creationTime: string | Date;
}

@Component({
  selector: 'app-sommelier-orders',
  templateUrl: './sommelier-orders.component.html',
  standalone:false,
  providers: [ListService]
})
export class SommelierOrdersComponent implements OnInit {

  orders: Order[] = [];
  orderStatuses: EnumDto[] = [];
  orderStatusOptions: { label: string; value: number | null }[] = []; // lo que usa el select
  selectedStatus: number | null = null;
  searchOrder = {} as GetOrderDto;
  dateRange = {
    start: null as Temporal.PlainDate | null,
    end: null as Temporal.PlainDate | null,
  };


  constructor(
    private orderService: OrderService,
    public readonly list: ListService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initializeDateRangeFromQuery();
    this.initializeStatusFromQuery();
    // Cargar estados de órdenes
    this.orderService.getOrderStatuses().subscribe(res => {
      this.orderStatuses = res;

      this.orderStatusOptions = [
        ...res.map(s => ({ label: s.name, value: s.value }))
      ];
    });

    const streamCreator = (query) => this.orderService.getSommelierOrders({ ...this.searchOrder, ...query });

    this.list.hookToQuery(streamCreator).subscribe((result: PagedResultDto<OrderDto>) => {
      // Mapear los resultados al formato que usa la UI
      this.orders = result.items.map(item => ({
        id: item.id,
        wineryName: item.userName || 'Unknown',
        status: this.formatOrderStatus(item.orderStatus),
        itemCount: item.itemCount || 0,
        totalPrice: item.total || 0,
        orderNumber: item.id.toString(),
        creationTime: item.creationTime,
        clientName: `${item.userName ?? ''} ${item.userSurname ?? ''}`.trim()
      }));
    });

  }

  onStatusChange(selected: number | null | undefined | string): void {
    const normalized =
      selected === null || selected === undefined || selected === '' ? null : selected;
    this.selectedStatus = normalized as number | null;
    this.searchOrder.orderStatus = this.selectedStatus;

    this.updateQueryParams();
    this.list.get();
  }

  onDateRangeChange(range: { start: Temporal.PlainDate | null; end: Temporal.PlainDate | null }): void {
    this.dateRange = {
      start: range.start,
      end: range.end
    };
    this.updateSearchOrderDateFields();
    this.updateQueryParams();
    this.list.get();
  }

  private initializeDateRangeFromQuery(): void {
    const params = this.route.snapshot.queryParams;
    const parsedStart = params['start'] ? this.parseDateFromQuery(params['start']) : null;
    const parsedEnd = params['end'] ? this.parseDateFromQuery(params['end']) : null;

    this.dateRange = {
      start: parsedStart,
      end: parsedEnd
    };

    this.updateSearchOrderDateFields();
  }

  private initializeStatusFromQuery(): void {
    const params = this.route.snapshot.queryParams;
    const statusParam = params['status'];
    
    if (statusParam !== null && statusParam !== undefined && statusParam !== '') {
      const statusValue = typeof statusParam === 'string' ? parseInt(statusParam, 10) : statusParam;
      if (!isNaN(statusValue)) {
        this.selectedStatus = statusValue;
        this.searchOrder.orderStatus = this.selectedStatus;
      }
    }
  }

  private updateSearchOrderDateFields(): void {
    if (this.dateRange.start) {
      const fromDate = new Date(
        this.dateRange.start.year,
        this.dateRange.start.month - 1,
        this.dateRange.start.day
      );
      this.searchOrder.fromCreationTime = fromDate.toISOString();
    } else {
      delete this.searchOrder.fromCreationTime;
    }

    if (this.dateRange.end) {
      const toDate = new Date(
        this.dateRange.end.year,
        this.dateRange.end.month - 1,
        this.dateRange.end.day,
        23,
        59,
        59,
        999
      );
      this.searchOrder.toCreationTime = toDate.toISOString();
    } else {
      delete this.searchOrder.toCreationTime;
    }
  }

  private updateQueryParams(): void {
    const queryParams: any = {};

    if (this.dateRange.start) {
      queryParams['start'] = this.formatDateForQuery(this.dateRange.start);
    } else {
      queryParams['start'] = null;
    }

    if (this.dateRange.end) {
      queryParams['end'] = this.formatDateForQuery(this.dateRange.end);
    } else {
      queryParams['end'] = null;
    }

    if (this.selectedStatus !== null && this.selectedStatus !== undefined) {
      queryParams['status'] = this.selectedStatus;
    } else {
      queryParams['status'] = null;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private formatDateForQuery(date: Temporal.PlainDate): string {
    return `${String(date.day).padStart(2, '0')}/${String(date.month).padStart(2, '0')}/${date.year}`;
  }

  private parseDateFromQuery(dateString: string): Temporal.PlainDate | null {
    try {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        return Temporal.PlainDate.from({ year, month, day });
      }
      if (dateString.includes('-')) {
        return Temporal.PlainDate.from(dateString);
      }
    } catch {
      return null;
    }
    return null;
  }

  private formatOrderStatus(status: OrderStatus | undefined): string {
    if (status === undefined) return 'Unknown';

    switch (status) {
      case OrderStatus.Pending:
        return '::OrderPending';
      case OrderStatus.Paid:
        return '::Paid';
      case OrderStatus.Shipped:
        return '::Shipped';
      case OrderStatus.Delivered:
        return '::Delivered';
      case OrderStatus.Cancelled:
        return '::Cancelled';
      default:
        return 'Unknown';
    }
  }
}
